/**
 * A single block on the Dashplan timeline.
 *
 * Renders at an absolute position set by the timeline. Tapping the body opens
 * the editor, dragging it moves the block, and the strips along the top and
 * bottom edges resize it from that end. The status circle toggles done without
 * opening anything.
 *
 * `use-block-gesture.ts` owns when a press becomes a drag; this file only
 * decides where the targets are. They are inside the block rather than
 * straddling its edges, so a block's handle can never sit on top of the
 * neighbour above or below it.
 */
import type { Doc } from "@convex/_generated/dataModel";
import { Check, Link2, MapPin, Timer } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatMinute, formatEndMinute, formatDuration } from "@/lib/dates";
import { KINDS, type DashplanKind } from "./constants";
import type { GestureMode } from "./use-block-gesture";

/** A Dashplan entry as returned by `api.dashplan.day`, with links resolved. */
export type TimelineEntry = Doc<"dashplanEntries"> & {
  linkedTaskTitle: string | null;
  linkedTaskStatus: "pending" | "completed" | null;
  linkedGoalTitle: string | null;
  focusSessionName: string | null;
  overlapsPrevious: boolean;
};

interface BlockProps {
  entry: TimelineEntry;
  top: number;
  height: number;
  column: number;
  columns: number;
  isDragging: boolean;
  displayStart: number;
  displayDuration: number;
  onGesture: (mode: GestureMode, event: React.PointerEvent) => void;
  onNudge: (mode: GestureMode, steps: number) => void;
  onSelect: () => void;
  onToggleStatus: () => void;
}

/**
 * A block shorter than this has no room for two handles and a tappable body,
 * so it gets the bottom one only — dragging the end is the common case, and
 * the start is still editable in the dialog.
 */
const BOTH_HANDLES_MIN_HEIGHT = 44;

/**
 * Edge strip that resizes the block.
 *
 * Arrow keys do the same thing one step at a time: a drag-only affordance is
 * unreachable from a keyboard, and this is the only place on the timeline
 * where duration can be changed without opening the dialog.
 */
function ResizeHandle({
  edge,
  label,
  short,
  onGesture,
  onNudge,
}: {
  edge: "top" | "bottom";
  label: string;
  short: boolean;
  onGesture: (mode: GestureMode, event: React.PointerEvent) => void;
  onNudge: (mode: GestureMode, steps: number) => void;
}) {
  const mode: GestureMode = edge === "top" ? "resize-start" : "resize-end";

  return (
    <button
      type="button"
      aria-label={label}
      // `touch-none` keeps the page from scrolling out from under a resize —
      // the handle is a deliberate target, so it never means "scroll".
      className={cn(
        // Stops short of the status circle, which owns its own column for
        // the block's full height and must stay tappable at both ends.
        "absolute left-0 right-6 z-20 flex items-center justify-center",
        "cursor-ns-resize touch-none group/handle",
        short ? "h-2" : "h-3",
        edge === "top" ? "top-0" : "bottom-0",
      )}
      onPointerDown={(event) => {
        event.stopPropagation();
        onGesture(mode, event);
      }}
      onKeyDown={(event) => {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
        event.preventDefault();
        // Auto-repeat outruns the round trip: every repeat would recompute
        // from the same server-side span and fire an identical mutation, so a
        // held key means thirty writes and one five-minute change. One step
        // per press instead.
        if (event.repeat) return;
        onNudge(mode, event.key === "ArrowUp" ? -1 : 1);
      }}
    >
      {/* The grip bar. Visible but quiet, so a full day does not read as a
          ladder; it firms up under the pointer and while dragging. */}
      <span
        className={cn(
          "h-0.5 w-6 rounded-full bg-current transition-opacity",
          "opacity-25 group-hover/handle:opacity-90 group-focus-visible/handle:opacity-90",
        )}
      />
    </button>
  );
}

export function DashplanBlock({
  entry,
  top,
  height,
  column,
  columns,
  isDragging,
  displayStart,
  displayDuration,
  onGesture,
  onNudge,
  onSelect,
  onToggleStatus,
}: BlockProps) {
  const kind = KINDS[entry.kind as DashplanKind] ?? KINDS.other;
  const Icon = kind.icon;
  const done = entry.status === "done";
  const skipped = entry.status === "skipped";

  // Below ~34px there is only room for one line, so the meta row is dropped.
  const compact = height < 34;
  const short = height < BOTH_HANDLES_MIN_HEIGHT;

  const widthPercent = 100 / columns;

  return (
    <div
      className={cn("absolute px-1 transition-shadow", isDragging && "z-30")}
      style={{
        top,
        height: Math.max(height, 20),
        left: `${column * widthPercent}%`,
        width: `${widthPercent}%`,
      }}
    >
      <div
        className={cn(
          "group relative h-full rounded-md border-l-2 overflow-hidden flex",
          "select-none [-webkit-touch-callout:none]",
          kind.accent,
          kind.surface,
          isDragging && "ring-2 ring-primary shadow-xl opacity-90",
          done && "opacity-60",
          skipped && "opacity-40 line-through",
        )}
      >
        <button
          type="button"
          onPointerDown={(event) => onGesture("move", event)}
          onClick={onSelect}
          // Names the action, not the gesture: "drag to reschedule" is not
          // something a screen reader user can act on, and the dialog this
          // opens is where they change the time instead.
          aria-label={`Open ${entry.title}`}
          // `pan-y` rather than `none`: blocks cover most of the timeline, so
          // a swipe across one still has to scroll the page. The gesture hook
          // takes the drag over from the scroller only once the long press has
          // fired, by which point the browser has not started scrolling.
          className={cn(
            "flex-1 min-w-0 text-left px-1.5 touch-pan-y",
            "cursor-grab active:cursor-grabbing hover:bg-background/30 transition-colors",
            // Clear of the handles, so a press near an edge resizes rather
            // than opening the editor.
            short ? "py-2" : "py-3",
          )}
        >
          <div className="flex items-start gap-1.5 min-w-0">
            <Icon className={cn("w-3 h-3 mt-0.5 shrink-0", kind.accent)} />
            <span
              className={cn(
                "text-xs font-medium leading-tight truncate min-w-0",
                done && "line-through",
              )}
            >
              {entry.title}
            </span>
          </div>

          {!compact && (
            <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground font-mono tabular-nums">
              <span>
                {formatMinute(displayStart)}–
                {formatEndMinute(displayStart + displayDuration)}
              </span>
              <span className="opacity-70">
                {formatDuration(displayDuration)}
              </span>

              {entry.linkedTaskId && (
                <Link2 className="w-2.5 h-2.5" aria-label="Linked to a task" />
              )}
              {entry.location && (
                <span className="flex items-center gap-0.5 truncate">
                  <MapPin className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate">{entry.location}</span>
                </span>
              )}
              {entry.actualFocusMinutes > 0 && (
                <span
                  className="flex items-center gap-0.5 text-primary"
                  title={`${entry.actualFocusMinutes} minutes of focus logged`}
                >
                  <Timer className="w-2.5 h-2.5" />
                  {entry.actualFocusMinutes}m
                </span>
              )}
            </div>
          )}
        </button>

        {/* Status toggle */}
        <button
          type="button"
          onClick={onToggleStatus}
          aria-label={done ? `Mark ${entry.title} as planned` : `Mark ${entry.title} as done`}
          aria-pressed={done}
          className="shrink-0 w-6 z-30 flex items-start justify-center pt-1 hover:bg-background/40 transition-colors"
        >
          <span
            className={cn(
              "w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-colors",
              done
                ? "bg-primary border-primary"
                : "border-muted-foreground/50 group-hover:border-foreground",
            )}
          >
            {done && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
          </span>
        </button>

        {!short && (
          <ResizeHandle
            edge="top"
            short={short}
            label={`Change when ${entry.title} starts`}
            onGesture={onGesture}
            onNudge={onNudge}
          />
        )}
        <ResizeHandle
          edge="bottom"
          short={short}
          label={`Change when ${entry.title} ends`}
          onGesture={onGesture}
          onNudge={onNudge}
        />
      </div>
    </div>
  );
}
